import { styled } from "goober";
import { useState } from "react";
import { queries } from "../../../../shared/queries";
import { useQuery } from "../../../hooks/useQuery";
import { deviceAuthPath } from "../../../lib/apiPaths";
import { apiUrl } from "../../../lib/tenantOrigin";
import { makeUrl, routes, useCurrentUrl } from "../../../lib/urls";
import { Button } from "../../Button";
import { Dropdown } from "../../Dropdown";
import { Icon } from "../../Icon";
import { Input } from "../../Input";
import { Link } from "../../Link";
import { ServiceAccountAvatar } from "../../ServiceAccountAvatar";
import { Text } from "../../Text";
import { Container, DropdownOpener, Header, ScrollArea } from "../shared";

const Section = styled("div")`
  padding: 16px 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 480px;
`;

const Field = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const CodeDisplay = styled("div")`
  font-family: var(--font-mono);
  font-size: 18px;
  letter-spacing: 2px;
  padding: 10px 12px;
  border-radius: 8px;
  background: var(--alpha8);
  color: var(--text-primary);
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--text-secondary);
  text-decoration: none;
  margin-bottom: 4px;
`;

type Mode = "user" | "service_account";

type Status =
  | { kind: "idle" }
  | { kind: "looking-up" }
  | { kind: "ready"; mode: Mode }
  | { kind: "submitting"; mode: Mode }
  | { kind: "approved" }
  | { kind: "denied" }
  | { kind: "error"; message: string };

async function postDevice(
  externalApiUrl: string,
  path: "lookup" | "approve" | "deny",
  body: Record<string, string>
): Promise<{ ok: boolean; status: number; data: Record<string, unknown> }> {
  const res = await fetch(`${externalApiUrl}${deviceAuthPath(path)}`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  return { ok: res.ok, status: res.status, data };
}

// Device-flow approval: a CLI prints a user code and sends the human here to
// authorize it. The flow has two modes -- acting as yourself ("user") or acting
// as a service account ("service_account"). Which one is determined by how the
// CLI started the flow; we look it up by code so the right form is shown. None
// of this can go through Zero -- device_authorizations is non-replicated (it
// holds a hashed device-code credential) -- so it posts to session-
// authenticated endpoints that enforce authorization server-side.
export function DeviceApproval() {
  const externalApiUrl = apiUrl();
  const { userCode: urlUserCode } = useCurrentUrl(
    routes.settings.members.serviceAccounts.device
  );
  const [accounts] = useQuery(queries.serviceAccounts());

  const [userCode, setUserCode] = useState(urlUserCode ?? "");
  const [serviceAccountId, setServiceAccountId] = useState("");
  const [status, setStatus] = useState<Status>({ kind: "idle" });

  const selected = accounts.find((a) => a.id === serviceAccountId);
  const hasCode = userCode.trim().length > 0;
  const mode =
    status.kind === "ready" || status.kind === "submitting"
      ? status.mode
      : null;
  const busy = status.kind === "submitting" || status.kind === "looking-up";

  const lookup = async () => {
    if (!hasCode) return;
    setStatus({ kind: "looking-up" });
    try {
      const {
        ok,
        status: code,
        data,
      } = await postDevice(externalApiUrl, "lookup", {
        user_code: userCode.trim(),
      });
      if (ok && (data.mode === "user" || data.mode === "service_account")) {
        setStatus({ kind: "ready", mode: data.mode });
        return;
      }
      setStatus({
        kind: "error",
        message:
          code === 404
            ? "That code is invalid or has expired."
            : ((data.error as string) ?? "We couldn’t check that code."),
      });
    } catch {
      setStatus({ kind: "error", message: "Couldn’t reach the server." });
    }
  };

  const submit = async (path: "approve" | "deny", onOk: Status) => {
    if (!mode) return;
    setStatus({ kind: "submitting", mode });
    const body: Record<string, string> = { user_code: userCode.trim() };
    if (mode === "service_account") body.service_account_id = serviceAccountId;
    try {
      const {
        ok,
        status: code,
        data,
      } = await postDevice(externalApiUrl, path, body);
      if (ok) {
        setStatus(onOk);
        return;
      }
      setStatus({
        kind: "error",
        message: (data.error as string) ?? `Something went wrong (${code}).`,
      });
    } catch {
      setStatus({ kind: "error", message: "Couldn’t reach the server." });
    }
  };

  const canSubmit =
    mode === "user" ||
    (mode === "service_account" && serviceAccountId.length > 0);

  return (
    <Container>
      <Header style={{ flexDirection: "column", alignItems: "flex-start" }}>
        <BackLink to={makeUrl(routes.settings.members.serviceAccounts)}>
          <Icon name="chevron-left" size={12} />
          <Text variant="text-small">Service accounts</Text>
        </BackLink>
        <Text variant="text-regular-md">Approve a device</Text>
      </Header>

      <ScrollArea>
        <Section>
          {status.kind === "approved" ? (
            <Field>
              <Text variant="text-regular-md">Device approved</Text>
              <Text variant="text-small" color="var(--text-secondary)">
                The device can now get a credential. You can close this page and
                return to your CLI.
              </Text>
            </Field>
          ) : status.kind === "denied" ? (
            <Field>
              <Text variant="text-regular-md">Device denied</Text>
              <Text variant="text-small" color="var(--text-secondary)">
                The request was turned down. The device won’t get a credential.
              </Text>
            </Field>
          ) : mode == null ? (
            <>
              {urlUserCode ? (
                <>
                  <Text variant="text-small" color="var(--text-secondary)">
                    Confirm this is the code shown by the CLI or workload
                    requesting access.
                  </Text>
                  <Field>
                    <Text variant="text-small-md">Device code</Text>
                    <CodeDisplay>{userCode}</CodeDisplay>
                  </Field>
                </>
              ) : (
                <>
                  <Text variant="text-small" color="var(--text-secondary)">
                    Enter the code shown by the CLI or workload requesting
                    access.
                  </Text>
                  <Field>
                    <Text variant="text-small-md">Device code</Text>
                    <Input
                      autoFocus
                      style={{ width: "100%", minWidth: 0 }}
                      placeholder="e.g. BCDF-GHJK"
                      value={userCode}
                      onChange={(e) => setUserCode(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") lookup();
                      }}
                    />
                  </Field>
                </>
              )}
              {status.kind === "error" && (
                <Text variant="text-small" color="var(--text-danger)">
                  {status.message}
                </Text>
              )}
              <div>
                <Button onClick={lookup} disabled={!hasCode || busy}>
                  {busy ? "Checking…" : "Continue"}
                </Button>
              </div>
            </>
          ) : mode === "user" ? (
            <>
              <Text variant="text-small" color="var(--text-secondary)">
                This lets the device act as you, with your own access, for a
                limited time. Only approve a code you started yourself.
              </Text>
              {status.kind === "error" && (
                <Text variant="text-small" color="var(--text-danger)">
                  {status.message}
                </Text>
              )}
              <div style={{ display: "flex", gap: 8 }}>
                <Button
                  onClick={() => submit("approve", { kind: "approved" })}
                  disabled={busy}
                >
                  {busy ? "Working…" : "Authorize as me"}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => submit("deny", { kind: "denied" })}
                  disabled={busy}
                >
                  Deny
                </Button>
              </div>
            </>
          ) : (
            <>
              <Text variant="text-small" color="var(--text-secondary)">
                Let a CLI or service act as one of your service accounts. Choose
                which one below.
              </Text>
              {accounts.length === 0 && (
                <Text variant="text-small" color="var(--text-secondary)">
                  You don’t manage any service accounts, so there’s nothing to
                  approve this device for.
                </Text>
              )}
              <Field>
                <Text variant="text-small-md">Act as</Text>
                <Dropdown
                  align="bottom-left"
                  opener={
                    <DropdownOpener>
                      <Text variant="text-regular">
                        {selected?.name ?? "Select a service account…"}
                      </Text>
                      <span style={{ opacity: 0.3 }}>
                        <Icon name="chevron-down" size={12} />
                      </span>
                    </DropdownOpener>
                  }
                  sections={[
                    {
                      items: accounts.map((a) => ({
                        type: "button" as const,
                        left: <ServiceAccountAvatar seed={a.id} size={16} />,
                        label: a.name,
                        right:
                          a.id === serviceAccountId ? (
                            <Icon
                              name="check"
                              size={12}
                              color="var(--text-secondary)"
                            />
                          ) : undefined,
                        onClick: () => setServiceAccountId(a.id),
                      })),
                    },
                  ]}
                />
              </Field>
              {status.kind === "error" && (
                <Text variant="text-small" color="var(--text-danger)">
                  {status.message}
                </Text>
              )}
              <div style={{ display: "flex", gap: 8 }}>
                <Button
                  onClick={() => submit("approve", { kind: "approved" })}
                  disabled={!canSubmit || busy}
                >
                  {busy ? "Working…" : "Approve"}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => submit("deny", { kind: "denied" })}
                  disabled={!canSubmit || busy}
                >
                  Deny
                </Button>
              </div>
            </>
          )}
        </Section>
      </ScrollArea>
    </Container>
  );
}
